import React from 'react';
import { useMutation } from '@apollo/client/react';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Divider } from 'common/components/Divider';
import { Text } from 'common/components/Text';
import {
    IContentTypeValue,
    IPortfolioItemFragment,
    IUpdatePortfolioItemInput,
    UpdatePortfolioItemDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { RelatedItemPortfolioItemForm } from 'user/components/forms';
import { PortfolioItemFileTable } from 'user/components/PortfolioItemFileTable';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { PortfolioItemDetailsView } from 'user/components/PortfolioItemDrawer/PortfolioItemDetailsView';

interface IProps {
    filesAreUpdating: boolean;
    hasMandatoryCertificates?: boolean;
    loading?: boolean;
    pageTitle?: string;
    portfolioItem: IPortfolioItemFragment;
    relatedObjectDescription?: string;
    userId?: string;
    withEditActions?: boolean;
    isCurrentUserItem?: boolean;
    onClose?(): void;
    onSubmit?(): void;
    setFilesAreUpdating(filesAreUpdating: boolean): void;
    children?: React.ReactNode;
}

export const PortfolioItemContentBase = ({
    filesAreUpdating,
    hasMandatoryCertificates,
    loading,
    pageTitle,
    portfolioItem,
    relatedObjectDescription,
    userId,
    withEditActions,
    isCurrentUserItem,
    children,
    onClose,
    onSubmit,
    setFilesAreUpdating,
}: IProps) => {
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [translate] = useTranslation();
    const [updatePortfolioItem] = useMutation(UpdatePortfolioItemDocument);

    const { id, description } = portfolioItem || {};

    const handleSubmit = async (values: IUpdatePortfolioItemInput) => {
        try {
            await updatePortfolioItem({
                variables: { id, portfolioItem: values },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('portfolioItemActionSuccess.update'), {
            variant: 'success',
        });

        onSubmit?.();

        return;
    };

    let descriptionView;

    if (
        !relatedObjectDescription &&
        portfolioItem.relatedType !== IContentTypeValue.Skill
    ) {
        if (isCurrentUserItem) {
            const initialValues = { description };

            descriptionView = (
                <RelatedItemPortfolioItemForm
                    initialValues={initialValues}
                    onClose={onClose}
                    onSubmit={handleSubmit}
                />
            );
        } else {
            descriptionView = !!description && (
                <Box sx={{ p: 4 }}>
                    <Text component="div">
                        <Box sx={{ lineHeight: '1.6' }}>{description}</Box>
                    </Text>
                </Box>
            );
        }
    }

    // Makes sure the title is mounted correctly
    const mountPageTitle = !!portfolioItem && !loading;

    const showFileTable =
        !portfolioItem ||
        (withEditActions && portfolioItem) ||
        (!withEditActions && !!portfolioItem?.files?.length);

    return (
        <>
            {mountPageTitle && (
                <PageTitle>
                    {`${portfolioItem.title}${
                        pageTitle ? `- ${pageTitle}` : ''
                    }`}
                </PageTitle>
            )}

            <Stack divider={<Divider />}>
                <PortfolioItemDetailsView
                    hasMandatoryCertificates={hasMandatoryCertificates}
                    loading={loading}
                    portfolioItem={portfolioItem}
                    relatedObjectDescription={relatedObjectDescription}
                    userId={userId}
                />

                {children}

                {descriptionView}

                {showFileTable && (
                    <PortfolioItemFileTable
                        filesAreUpdating={filesAreUpdating}
                        portfolioItem={portfolioItem}
                        setFilesAreUpdating={setFilesAreUpdating}
                        userId={userId}
                        withEditActions={withEditActions}
                    />
                )}
            </Stack>
        </>
    );
};
